// src/pages/RegisterForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CountrySelect } from 'react-country-state-city';
import "react-country-state-city/dist/react-country-state-city.css";
import api from '../../api';
import InputField from '../../components/InputField';
import RegistrationSuccessModal from '../../components/modals/RegistrationSuccessModal';
import PhoneVerificationModal from '../profile/PhoneVerificationModal';
import { useTranslation, Trans } from 'react-i18next';
import SEO from '../../components/SEO';


// Mappatura dei numeri per alcuni paesi (senza prefisso)
const phoneNumberLengths = {
  IT: 10, // Italia
  US: 10, // Stati Uniti
  GB: 10, // Regno Unito
};

function RegisterForm() {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    birthday: '',
    username: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    email: '',
    city: '',
    countryName: '',
    countryEmoji: '',
    dialCode: '',
    countryIso2: ''
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Funzioni per il controllo dinamico di username ed email
  const checkUsername = async (username) => {
    try {
      const { data } = await api.get(`/auth/check-username?username=${username}`);
      if (data.exists) {
        setErrors(prev => ({ ...prev, username: t('register.usernameExists') }));
      } else {
        setErrors(prev => ({ ...prev, username: '' }));
      }
    } catch (error) {
    }
  };

  const checkEmail = async (email) => {
    try {
      const { data } = await api.get(`/auth/check-email?email=${email}`);
      if (data.exists) {
        setErrors(prev => ({ ...prev, email: t('register.emailExists') }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    } catch (error) {
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Rimuovo eventuali errori se l'utente inizia a digitare
    if (value.trim() !== '' && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Gestione specifica per il campo telephone:
    // La logica rimane invariata per i test (in futuro abiliteremo la verifica)
    if (name === "telephone" && formData.dialCode) {
      const prefix = `+${formData.dialCode}`;
      if (isPhoneVerified) return;
      if (!value.startsWith(prefix)) return;
      const numericPart = value.slice(prefix.length);
      if (numericPart && !/^\d*$/.test(numericPart)) return;
      const maxDigits = phoneNumberLengths[formData.countryIso2] || 10;
      if (numericPart.length > maxDigits) return;
    }

    const updatedData = { ...formData, [name]: value };
    setFormData(updatedData);

    if (name === "password") {
      checkPasswordStrength(value);
    }

    if (name === "confirmPassword") {
      setPasswordsMatch(updatedData.password === value);
    }
  };

  const handleCountryChange = (val) => {
    const prefix = `+${val.phone_code}`;
    setFormData(prevData => ({
      ...prevData,
      countryName: val.name,
      countryEmoji: val.emoji,
      dialCode: val.phone_code,
      countryIso2: val.iso2,
      telephone: prevData.telephone.startsWith(prefix) ? prevData.telephone : prefix
    }));
  };

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 1;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthLabel = () => {
    switch (passwordStrength) {
      case 1:
      case 2:
        return t('register.low');
      case 3:
        return t('register.medium');
      case 4:
        return t('register.strong');
      case 5:
        return t('register.max');
      default:
        return '';
    }
  };

  // Funzione di validazione che controlla tutti i campi del form
  const validateForm = () => {
    let newErrors = {};
    if (!formData.username.trim()) newErrors.username = t('register.requiredField');
    if (!formData.email.trim()) newErrors.email = t('register.requiredField');
    if (!formData.password.trim()) {
      newErrors.password = t('register.requiredField');
    } else if (passwordStrength < 3) {
      newErrors.password = t('register.weakPassword');
    }
    if (!formData.confirmPassword.trim()) newErrors.confirmPassword = t('register.requiredField');
    if (!formData.name.trim()) newErrors.name = t('register.requiredField');
    if (!formData.surname.trim()) newErrors.surname = t('register.requiredField');
    if (!formData.birthday.trim()) {
      newErrors.birthday = t('register.requiredField');
    } else {
      const today = new Date();
      const birthday = new Date(formData.birthday);
      let age = today.getFullYear() - birthday.getFullYear();
      const m = today.getMonth() - birthday.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
        age--;
      }
      if (age < 18) {
        newErrors.birthday = t('register.underAge');
      }
    }
    if (!formData.city.trim()) newErrors.city = t('register.requiredField');

    // Validazione per il campo telephone
    if (!formData.telephone.trim()) {
      newErrors.telephone = t('register.requiredField');
    } else {
      const prefix = `+${formData.dialCode}`;
      if (!formData.telephone.startsWith(prefix)) {
        newErrors.telephone = t('register.invalidPhonePrefix');
      } else {
        const numericPart = formData.telephone.slice(prefix.length);
        const requiredLength = phoneNumberLengths[formData.countryIso2] || 10;
        if (numericPart.length !== requiredLength) {
          newErrors.telephone = t('register.invalidPhoneLength');
        }
      }
    }

    if (!formData.countryName.trim()) newErrors.countryName = t('register.requiredField');
    if (!passwordsMatch) newErrors.confirmPassword = t('register.passwordMismatch');

    // Validazione per i checkbox obbligatori
    if (!checkbox1) newErrors.checkbox1 = t('register.acceptTerms');
    if (!checkbox2) newErrors.checkbox2 = t('register.acceptPrivacy');

    return newErrors;
  };

  const isFormValid = () => {
    return Object.keys(validateForm()).length === 0;
  };

  // Funzione che completa la registrazione inviando i dati al backend
  const completeRegistration = async () => {
    const payload = { ...formData, fullPhoneNumber: formData.telephone, isPhoneVerified: true };
    try {
      const response = await api.post('/auth/register', payload);
      localStorage.setItem('authToken', response.data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setIsModalOpen(true);
      setRegistrationError('');
    } catch (error) {
      setRegistrationError(t('register.registrationFailed'));
    }
  };

  // Funzione invocata quando il numero viene verificato nel modal
  const handlePhoneVerified = () => {
    setIsPhoneVerified(true);
    setShowPhoneVerificationModal(false);
    completeRegistration();
  };

  // Gestione del submit del form
  // SOSTITUISCI handleSubmit CON QUESTA VERSIONE:
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);
    setRegistrationError('');

    // Se ci sono errori di validazione base, blocca
    if (Object.keys(validationErrors).length > 0) {
      setRegistrationError(t('register.formInvalid'));
      return;
    }

    // Verifica username ed email solo ora
    let usernameExists = false;
    let emailExists = false;

    try {
      const usernameRes = await api.get(`/auth/check-username?username=${formData.username}`);
      if (usernameRes.data.exists) {
        usernameExists = true;
      }
    } catch (error) {
    }

    try {
      const emailRes = await api.get(`/auth/check-email?email=${formData.email}`);
      if (emailRes.data.exists) {
        emailExists = true;
      }
    } catch (error) {
    }

    // Se username o email esistono, mostra errore solo su quei campi
    if (usernameExists || emailExists) {
      setErrors(prev => ({
        ...prev,
        ...(usernameExists ? { username: t('register.usernameExists') } : {}),
        ...(emailExists ? { email: t('register.emailExists') } : {})
      }));
      setRegistrationError(t('register.formInvalid'));
      return;
    }

    // Procedi come prima
    if (!isPhoneVerified) {
      setShowPhoneVerificationModal(true);
    } else {
      completeRegistration();
    }
  };


  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate('/login', { state: { fromRegistration: true } });
  };


  // Funzione per il rinvio della email
  const handleResendConfirmationEmail = async () => {
    if (isButtonDisabled) return;
    try {
      await api.post('/auth/resend-confirmation-email', { email: formData.email });
      alert(t('register.emailResent'));
      // Imposta il bottone come disabilitato (il countdown partirà dall'useEffect)
      setIsButtonDisabled(true);
    } catch (error) {
      setRegistrationError(t('register.resendEmailError'));
    }
  };

  // useEffect per gestire il countdown
  useEffect(() => {
    let interval = null;
    if (isButtonDisabled) {
      // Quando il bottone viene disabilitato, inizializza il timer a 60
      setResendTimer(60);
      interval = setInterval(() => {
        setResendTimer(prevTimer => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            setIsButtonDisabled(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    // Pulizia dell'intervallo
    return () => clearInterval(interval);
  }, [isButtonDisabled]);


  const getTelephoneMaxLength = () => {
    if (formData.dialCode && formData.countryIso2) {
      return (`+${formData.dialCode}`).length + (phoneNumberLengths[formData.countryIso2] || 10);
    }
    return undefined;
  };


  const goToTerms = () => {
    navigate('/terms');
  };

  const goToPrivacy = () => {
    navigate('/privacy');
  };

  return (
    <>
      <SEO
        title="Registrati - Save Your Travel"
        description="Registrati alla piattaforma Save Your Travel e crea il tuo account per vendere i tuoi viaggi, gestire le prenotazioni e accedere a offerte di viaggio last minute e low cost."
        keywords={[
          "Registrazione",
          "Account",
          "prenotazioni last minute",
          "viaggi low cost",
          "rivendita viaggi"
        ]}
        frequency="week"
        priority="0.6"
      />
      <div className="container my-32">
        <RegistrationSuccessModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onResendEmail={handleResendConfirmationEmail}
          isButtonDisabled={isButtonDisabled}
          resendTimer={resendTimer}
          phoneNumber={formData.telephone}
        />
        <PhoneVerificationModal
          phoneNumber={formData.telephone}
          isOpen={showPhoneVerificationModal}
          onClose={() => setShowPhoneVerificationModal(false)}
          onVerified={handlePhoneVerified}
        />
        <div className="uppercase font-heading text-3xl ml-4 text-start font-semibold mb-8 border-b-2 pb-8">
          {t('register.title')}
        </div>

        {registrationError && (
          <div className="bg-red-100 text-red-700 p-3 mb-4 rounded">
            {registrationError}
          </div>
        )}

        <div className="items-center lg:w-2/3 mx-auto bg-gray-100">
          <div className="lg:p-12 p-6 bg-white shadow-md rounded-xl">
            <div className="pb-8">
              <h2 className="font-heading text-2xl mb-2 uppercase text-wild-ocean font-semibold">
                {t('register.title2')}
              </h2>
              <label className="text-sm">{t('register.login')} </label>
              <Link to="/login" className="text-wild-ocean underline text-sm hover:text-green-adventure">
                {t('register.loginLink')}
              </Link>
            </div>
            <form onSubmit={handleSubmit}>
              <InputField
                name="username"
                label={t('register.username')}
                value={formData.username}
                handleChange={handleChange}
                error={!!errors.username}
                errorText={errors.username}
                required
              />
              <InputField
                type="email"
                name="email"
                label={t('register.email')}
                value={formData.email}
                handleChange={handleChange}
                error={!!errors.email}
                errorText={errors.email}
                required
              />
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <InputField
                    type="password"
                    name="password"
                    label={t('register.password3')}
                    value={formData.password}
                    handleChange={handleChange}
                    error={!!errors.password}
                    errorText={errors.password}
                    required
                    helperText={t('register.passwordHelper')}
                  />
                </div>
                <span className="text-gray-400 px-2 self-center">|</span>
                <div className="flex-1">
                  <InputField
                    type="password"
                    name="confirmPassword"
                    label={t('register.password')}
                    value={formData.confirmPassword}
                    handleChange={handleChange}
                    error={!!errors.confirmPassword}
                    errorText={errors.confirmPassword}
                    required
                    helperText={t('register.passwordMatchHelper')}
                  />
                </div>
              </div>
              <div className="mb-2">
                <p className="text-sm font-heading uppercase">{getPasswordStrengthLabel()}</p>
                <div className="flex justify-between">
                  <div className="relative w-1/3 h-2 bg-gray-300 rounded-xl">
                    <div
                      className={`absolute top-0 left-0 h-2 rounded-xl transition-all duration-500 ${passwordStrength === 1 ? 'bg-red-500 w-1/5'
                        : passwordStrength === 2 ? 'bg-orange-500 w-2/5'
                          : passwordStrength === 3 ? 'bg-yellow-500 w-3/5'
                            : passwordStrength === 4 ? 'bg-green-500 w-4/5'
                              : passwordStrength === 5 ? 'bg-blue-500 w-full'
                                : 'w-0'
                        }`}
                    />
                  </div>
                  {!passwordsMatch && (
                    <p className="text-red-500 text-sm font-heading uppercase">
                      {t('register.passwordMismatch')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-6 pt-2">
                <div className="flex-1">
                  <InputField
                    name="name"
                    label={t('register.name')}
                    value={formData.name}
                    handleChange={handleChange}
                    error={!!errors.name}
                    errorText={errors.name}
                    required
                  />
                </div>
                <span className="text-gray-400 pb-8">|</span>
                <div className="flex-1">
                  <InputField
                    name="surname"
                    label={t('register.surname')}
                    value={formData.surname}
                    handleChange={handleChange}
                    error={!!errors.surname}
                    errorText={errors.surname}
                    required
                  />
                </div>
              </div>
              <InputField
                type="date"
                name="birthday"
                label={t('register.birthday')}
                value={formData.birthday}
                handleChange={handleChange}
                error={!!errors.birthday}
                errorText={errors.birthday}
                required
              />
              <div className="flex items-center gap-6">
                <div className="flex-1">
                  <InputField
                    name="city"
                    label={t('register.city')}
                    value={formData.city}
                    handleChange={handleChange}
                    error={!!errors.city}
                    errorText={errors.city}
                    required
                  />
                </div>
                <span className="text-gray-400 pb-8">|</span>
                <div className="flex-1">
                  <div className="mb-4">
                    <CountrySelect
                      onChange={handleCountryChange}
                      placeHolder={t('register.countryName')}
                      containerClassName="country-select-container"
                      inputClassName="country-select-input"
                      optionClass="country-select-option"
                    />
                    {errors.countryName && (
                      <p className="text-xs text-red-500 mt-1">{errors.countryName}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="relative">
                <InputField
                  type="tel"
                  name="telephone"
                  label={t('register.telephone')}
                  value={formData.telephone}
                  handleChange={handleChange}
                  error={!!errors.telephone}
                  errorText={errors.telephone}
                  required
                  maxLength={getTelephoneMaxLength()}
                  disabled={isPhoneVerified}
                />
              </div>
              <div className="lg:my-16 my-10">
                <div className="flex flex-col">
                  <div className="flex items-start mb-2">
                    <input
                      type="checkbox"
                      id="checkbox1"
                      name="checkbox1"
                      className="mr-2 lg:w-6 lg:h-6 w-12 h-10"
                      checked={checkbox1}
                      onChange={e => setCheckbox1(e.target.checked)}
                    />
                    <label htmlFor="checkbox1" className="text-sm">
                      <Trans
                        i18nKey="register.check"
                        components={{
                          terms: (
                            <span
                              className="text-wild-ocean underline font-semibold cursor-pointer"
                              onClick={goToTerms}
                            />
                          ),
                          privacy: (
                            <span
                              className="text-wild-ocean underline font-semibold cursor-pointer"
                              onClick={goToPrivacy}
                            />
                          )
                        }}
                      />
                    </label>
                  </div>
                  <div className="flex flex-col mt-4">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="checkbox2"
                        name="checkbox2"
                        className="mr-4 lg:w-6 lg:h-6 w-10 h-10"
                        checked={checkbox2}
                        onChange={e => setCheckbox2(e.target.checked)}
                      />
                      <label htmlFor="checkbox2" className="text-sm">
                        <Trans
                          i18nKey="register.check2"
                          components={{ b: <b /> }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="
                lg:w-1/2 w-full mx-auto text-white flex justify-center 
                uppercase font-heading font-semibold px-4 py-2 rounded-lg shadow-md 
                transition-colors duration-300 bg-wild-ocean hover:bg-green-adventure
              "
              >
                {t('register.register')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>

  );
}

export default RegisterForm;
